import React, { ReactElement, RefObject } from 'react';

import { useTranslation } from 'react-i18next';
import { ContentHeadline } from '../../../_lib/components/ContentHeadline';
import { ContentParagraph, ContentParagraphModifier } from '../../../_lib/components/ContentParagraph';
import { LinkedTeaserImageTopHeadlines } from '../../../components/molecules/LinkedTeaserImageInLists';
import { LinkedTeaserImageTopHeadlinesModifier } from '../../../components/molecules/LinkedTeaserImageInLists/LinkedTeaserImageTopHeadlines';
import { RESPONSIVE_TYPE } from '../../../components/molecules/Image/Responsive';
import { Namespace } from '../../../translation/namespace';
import { DateRanger } from '../../../components/molecules/DateRanger/DateRanger';
import TopHeadlineInteractions from './TopHeadlineInteractions';
import { Breakpoints } from '../../../types/Breakpoints';
import { TopHeadlineImage, TopHeadlineImageSmall } from '../../../types/api/ImageType';
import { TopHeadline } from '../TopHeadlineType';
import { formatDate, isDateBefore, TimeFormats } from '../../../lib/DateFormat';
import { VideoWithMetaInfo } from '../../../_lib/components/Video/VideoWithMetaInfoType';
import { ContentHeadlineModifier } from '../../../_lib/components/ContentHeadline/ContentHeadline';

export type ImageWithWidth = (TopHeadlineImage | TopHeadlineImageSmall) & { maxWidth?: number; minWidth?: number };

type TopHeadlineItem = TopHeadline & {
  index: number;
  listItemRef?: RefObject<HTMLLIElement | null> | undefined;
  editView: boolean;
};

export default function TopHeadlineItem({
  image,
  video,
  title,
  text,
  titleUrl,
  featured,
  id,
  startDate,
  endDate,
  isAllDay,
  isCurrentlyVisible,
  index,
  listItemRef,
  editView,
  editForm,
  deleteForm,
  fetchUrl,
}: TopHeadlineItem) {
  const { t } = useTranslation(Namespace.TopHeadlines);
  const isMultiDayEvent = (start: string, end: string): boolean => {
    return formatDate(start, 'DD.MM.YYYY') !== formatDate(end, TimeFormats.DAY_MONTH_YEAR) && isDateBefore(start, end);
  };

  /**
   * We have two different top headline image styles: large if featured and small all the others.
   * We decided to destruct the image object in the frontend because we need different props
   * for the small image. In the backend it's more complicated to do that.
   * As we want to just render it in 320 and it should use the same route created for all top headline image.
   * @returns imageObject
   */
  const responsiveImage = (img: ImageWithWidth): ImageWithWidth => {
    if (!featured) {
      img.maxWidth = Breakpoints.s_smartphone_portrait;
      img.imageType = RESPONSIVE_TYPE.topHeadlineImageSmall;
    } else {
      img.maxWidth = Breakpoints.s_tablet_landscape;
    }
    return img;
  };

  const topHeadlineContent = (): ReactElement => (
    <div className="top-headlines__content">
      {image ? (
        <div className="top-headlines__image-video-container">
          <LinkedTeaserImageTopHeadlines
            image={responsiveImage(image)}
            pageLink={titleUrl}
            fetchUrl={fetchUrl}
            modifier={LinkedTeaserImageTopHeadlinesModifier.NO_GRID}
          />
        </div>
      ) : null}
      {video ? (
        <div className="top-headlines__image-video-container">
          <VideoWithMetaInfo {...video} />
        </div>
      ) : null}
      <article className="top-headlines__article">
        <div className="top-headlines__text-container">
          <div className="top-headlines__headline">
            <ContentHeadline
              hierarchy={editView ? 4 : 3}
              url={titleUrl}
              title={title}
              modifier={featured ? ContentHeadlineModifier.TOP_HEADLINE_PRIMARY : ContentHeadlineModifier.TOP_HEADLINE}
            />
          </div>
          {text ? (
            <div className="top-headlines__content-paragraph">
              <ContentParagraph
                // different font size in TopHeadlinesBlock
                modifier={ContentParagraphModifier.TOP_HEADLINE}
              >
                {text}
              </ContentParagraph>
              {editView && startDate && endDate && (
                <ContentParagraph>
                  <dfn className="top-headlines__time-period">{t('timePeriod')}</dfn>:{' '}
                  <DateRanger
                    start={startDate}
                    end={endDate}
                    multiDayEvent={isMultiDayEvent(startDate, endDate)}
                    allDayEvent={isAllDay || false}
                  />
                </ContentParagraph>
              )}
            </div>
          ) : null}
        </div>
      </article>
    </div>
  );

  return (
    <li
      className={featured ? 'top-headlines__item top-headlines__item--featured' : 'top-headlines__item'}
      id={id}
      key={id}
      ref={listItemRef}
    >
      {editView ? (
        <div className="top-headlines__editview-container">
          <TopHeadlineInteractions
            id={`top-headline-interactions-${id}`}
            title={title}
            editForm={editForm}
            deleteForm={deleteForm}
            isCurrentlyVisible={isCurrentlyVisible}
            index={index}
            image={image}
            video={video}
          />
          {topHeadlineContent()}
        </div>
      ) : (
        topHeadlineContent()
      )}
    </li>
  );
}
